import { CdkScrollable } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, Inject, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';

import { MtnaLogger } from '@mtna/lib-ui';
import { trackByCoreResource, Variable, VariableSummary } from '@mtna/pojo-consumer-ui';
import { RdsVariableUtil } from '../core/utils/variable-util';

export class RdsVariableSelectionDialogData<V extends Variable | VariableSummary = Variable> {
  /**
   * @param variables All variables to choose from
   * @param title Title for the dialog, defaults to "Select a variable"
   * @param listStyles Optional ngStyles for the scrollable list, defaults to `{ height: 'calc(65vh - 36px - 56px)', maxHeight: '500px' }`.
   * The calc subtracts the height of the searchbox and paginator.
   */
  constructor(
    public variables: Array<V>,
    public title: string = 'Select a variable',
    public listStyles: { [key: string]: string } = { height: 'calc(65vh - 36px - 56px)', maxHeight: '500px' }
  ) {}
}

/**
 * Dialog for selecting a variable.
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-variable-selection-dialog',
  templateUrl: './variable-selection-dialog.component.html',
  styleUrls: [`./variable-selection-dialog.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsVariableSelectionDialogComponent<V extends Variable | VariableSummary = Variable> {
  /** the scrollable list when many codes exist. Used for scrollTop on pagination and search */
  @ViewChild(CdkScrollable) scrollContainer: CdkScrollable;

  /** Variables to displace on a single page */
  displayedVariables: Array<V> = [];
  /** Variables matching the user search */
  filteredVariables: Array<V> = [];
  /** Index of the current page */
  pageIndex = 0;
  /** Number of variables to show on a single page */
  pageSize = 25;
  /** The variable the user selected */
  selectedVariable: V;

  /** TrackBy function for the *ngFor loop of codes */
  trackByCr = trackByCoreResource;

  constructor(@Inject(MAT_DIALOG_DATA) public data: RdsVariableSelectionDialogData<V>, private logger: MtnaLogger) {
    if (!!this.data && !!this.data.variables) {
      this.filteredVariables = data.variables.slice();
      this.displayedVariables = data.variables.slice(0, this.pageSize);
    } else {
      this.logger.error('[RdsVariableSelectionDialogComponent] constructor - missing variables');
    }
  }

  paginate(page: PageEvent) {
    this.pageIndex = page.pageIndex;
    this.pageSize = page.pageSize;
    const offset = page.pageIndex * page.pageSize;
    this.displayedVariables = this.filteredVariables.slice(offset, offset + this.pageSize);
  }

  search(text: string) {
    // When a new text search is initiated, start at the first page
    this.pageIndex = 0;

    if (!!text) {
      this.filteredVariables = this.data.variables.filter((v) => RdsVariableUtil.filterVariable(text, v));
    } else {
      this.filteredVariables = this.data.variables.slice();
    }

    // Set the contents of the first page
    this.displayedVariables = this.filteredVariables.slice(0, this.pageSize);
  }

  _scrollToTop() {
    if (!!this.scrollContainer) {
      this.scrollContainer.scrollTo({ top: 0 });
    }
  }
}
